import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack } from "@mui/material";
import { observer } from "mobx-react";
import { FC, useCallback, useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { useStore } from "../../../hooks";
import { IFilters } from "../../../store/goods-store";
import { clearEmptyFieldiInObject } from "../../../utils/utils";
import yup from "../../../yup-extended";
import { MuiFormInput, MuiFormRadioGroup, MuiFormSelect } from "../../form-controls";
import StyledButton from "../../ui/StyledButton";
import styles from "../styles";

enum RadioGroupValue {
  all = "all",
  active = "active",
  notActive = "notActive",
}

interface IFilterData extends Omit<IFilters, "active"> {
  active?: RadioGroupValue;
}

const GoodsFilters: FC = () => {
  const {
    goodsStore: { setFilters, fetchGoods, filters },
    referenceBooks: { clientIds, fetchClientIds }
  } = useStore();

  const schema = yup.object({
    wbClientId: yup.string(),
    ozonClientId: yup.string(),
    wbArticle: yup.string(),
    ozonSku: yup.string(),
    manager: yup.string(),
    active: yup
    .string()
    .oneOf<RadioGroupValue>([
      RadioGroupValue.all,
      RadioGroupValue.active,
      RadioGroupValue.notActive,
    ]),
  });

  const methods = useForm<IFilterData>({
    resolver: yupResolver<IFilterData>(schema),
    defaultValues: {
      wbClientId: filters?.wbClientId || "",
      ozonClientId: filters?.ozonClientId || "",
      wbArticle: filters?.wbArticle || "",
      ozonSku: filters?.ozonSku || "",
      manager: filters?.manager || "",
      active:
        filters?.active === false
          ? RadioGroupValue.notActive
          : filters?.active === true
            ? RadioGroupValue.active
            : RadioGroupValue.all,
    },
  });

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = methods;

  const radioGroupItems = useMemo(
    () => [
      { label: "Все", value: "all" },
      { label: "Да", value: "active" },
      { label: "Нет", value: "notActive" },
    ],
    []
  );

  const isEmptyFilters = Object
  .keys(filters)
  .filter(key => filters[key as keyof IFilters] !== undefined).length === 0;

  const onSubmit: SubmitHandler<IFilterData> = (data) => {
    const transformedData = clearEmptyFieldiInObject<IFilters>(data);

    let activeValue = undefined;
    if (data.active !== undefined && data.active !== "all") {
      activeValue = data.active === RadioGroupValue.active;
    }

    setFilters({ ...transformedData, active: activeValue });
  };

  const onReset = useCallback(() => {
    setValue("ozonClientId", "");
    setValue("wbClientId", "");
    setValue("active", RadioGroupValue.all);
    setValue("manager", "");
    setValue("ozonSku", "");
    setValue("wbArticle", "");

    setFilters({});
  }, [setValue, setFilters]);

  useEffect(() => {
    if (!clientIds) {
      fetchClientIds();
    }
  }, [clientIds, fetchClientIds]);

  useEffect(() => {
    fetchGoods();
  }, [filters, fetchGoods]);

  return (
    <Box sx={styles.filterWrapper}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="row" gap="12px" alignItems="center">
          <MuiFormSelect
            placeholder="ID WB"
            variant="standard"
            options={clientIds?.WB || []}
            error={errors.wbClientId}
            name="wbClientId"
            control={control}
            size="s"
          />

          <MuiFormSelect
            placeholder="ID OZON"
            variant="standard"
            options={clientIds?.OZON || []}
            error={errors.ozonClientId}
            name="ozonClientId"
            control={control}
            size="s"
          />

          <MuiFormInput
            name="wbArticle"
            control={control}
            placeholder="Артикул WB"
            error={!!errors?.wbArticle}
            helperText={errors?.wbArticle?.message}
            size="small"
          />

          <MuiFormInput
            name="ozonSku"
            control={control}
            placeholder="OZON SKU"
            error={!!errors?.ozonSku}
            helperText={errors?.ozonSku?.message}
            size="small"
          />

          <MuiFormInput
            name="manager"
            control={control}
            placeholder="Менеджер"
            error={!!errors?.manager}
            helperText={errors?.manager?.message}
            size="small"
          />

          <MuiFormRadioGroup
            control={control}
            name="active"
            error={errors.active}
            label="Активный:"
            items={radioGroupItems}
          />

          <StyledButton type="submit" size="small">
            Применить
          </StyledButton>

          <StyledButton onClick={onReset} variant="outlined" size="small" disabled={isEmptyFilters}>
            Очистить фильтры
          </StyledButton>
        </Stack>
      </form>
    </Box>
  );
};

export default observer(GoodsFilters);
